import { z } from "zod";
import { localizedTextSchema, requiredLocalizedTextSchema, timestampsSchema } from "./common";
import { listQueryBaseSchema, queryBoolean } from "./list-query";
import { mediaDtoSchema } from "./media";

export const BLOG_STATUS = {
  DRAFT: 1,
  SCHEDULED: 2,
  PUBLISHED: 3,
} as const;
export type BlogStatus = (typeof BLOG_STATUS)[keyof typeof BLOG_STATUS];

const blogStatusSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

/** Accepts either the numeric enum or the human-friendly string (used by the
 *  CLI ingest path). */
const blogStatusInputSchema = z
  .union([
    blogStatusSchema,
    z.enum(["draft", "scheduled", "published"]),
  ])
  .transform((v) => {
    if (typeof v === "number") return v;
    if (v === "draft") return BLOG_STATUS.DRAFT;
    if (v === "scheduled") return BLOG_STATUS.SCHEDULED;
    return BLOG_STATUS.PUBLISHED;
  });

export const blogDtoSchema = z
  .object({
    id: z.string().uuid(),
    title: localizedTextSchema,
    content: localizedTextSchema,
    shortDescription: localizedTextSchema,
    metaDescription: localizedTextSchema,
    slug: z.string(),
    keywords: z.array(z.string()),
    hashtags: z.array(z.string()),
    heroMedia: mediaDtoSchema.nullable(),
    galleryMedia: z.array(mediaDtoSchema).default([]),
    /** Optional category link. `category` is the joined name pair for the chip. */
    categoryId: z.string().uuid().nullable(),
    category: z
      .object({ id: z.string().uuid(), name: localizedTextSchema, slug: z.string().nullable() })
      .nullable(),
    status: blogStatusSchema,
    publishedAt: z.string().nullable(),
    readMinutes: z.number().int().nullable(),
    featured: z.boolean(),
    /** External hero URL — falls back to `heroMedia` if blank. */
    heroImageUrl: z.string().nullable(),
  })
  .extend(timestampsSchema.shape);
export type BlogDTO = z.infer<typeof blogDtoSchema>;

export const blogUpsertSchema = z.object({
  title: requiredLocalizedTextSchema,
  content: localizedTextSchema,
  shortDescription: localizedTextSchema,
  metaDescription: localizedTextSchema,
  slug: z.string().optional(),
  keywords: z.array(z.string()).default([]),
  hashtags: z.array(z.string()).default([]),
  categoryId: z.string().uuid().nullable().optional(),
  status: blogStatusInputSchema.default(BLOG_STATUS.DRAFT),
  publishedAt: z.string().nullable().optional(),
  readMinutes: z.number().int().nonnegative().nullable().optional(),
  featured: z.boolean().default(false),
  heroImageUrl: z.string().url().nullable().optional(),
});
export type BlogUpsertInput = z.infer<typeof blogUpsertSchema>;

/**
 * Superset of `blogUpsertSchema` consumed by the CLI ingestion path. Identical
 * shape today; kept as a separate export so the CLI / brief documentation can
 * import a stable name and we can diverge later (e.g. add `dryRun: true`).
 */
export const blogIngestSchema = blogUpsertSchema;
export type BlogIngestInput = z.infer<typeof blogIngestSchema>;

export const blogsListQuerySchema = listQueryBaseSchema.extend({
  /** When set, filter by exact status. */
  status: z.coerce.number().int().optional(),
  /** When `true`, only return rows visible to the public site. */
  publishedOnly: queryBoolean(),
  categoryId: z.string().uuid().optional(),
  featured: queryBoolean(),
});
export type BlogsListQuery = z.infer<typeof blogsListQuerySchema>;
